import React from 'react';
import { motion } from 'framer-motion';
import { FiHome, FiBriefcase, FiBarChart2, FiArrowRight } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import TiltCard from '../common/TiltCard';

const services = [
  {
    id: 'residential',
    icon: FiHome,
    title: 'Residential Appraisals',
    description: 'Our licensed appraisers deliver fast, accurate, and unbiased valuations for homes of all sizes. Whether buying, selling, refinancing, or settling an estate, you can count on our residential appraisal services to provide clarity and confidence in every transaction.',
    bullets: [
      'Detailed, market-driven property assessments',
      'Accepted by leading lenders and institutions',
      'Quick turnaround times for your important decisions'
    ]
  },
  {
    id: 'commercial',
    icon: FiBriefcase,
    title: 'Commercial Appraisals',
    description: 'Our team, led by an MAI Designated chief appraiser, provides comprehensive, impartial valuations for office buildings, retail properties, industrial facilities, and multifamily assets. With expertise in local markets and regulatory standards, we ensure your commercial property appraisal supports strategic decisions and regulatory compliance.',
    bullets: [
      'Detailed analysis using current market and income approaches',
      'Compliant with industry and lender requirements',
      'Fast, reliable turnaround for all project sizes'
    ]
  },
  {
    id: 'investment',
    icon: FiBarChart2,
    title: 'Investment Analysis',
    description: 'Gain a sharper edge in real estate with data-driven investment insights tailored to your goals. Our expert analyses evaluate risk, return, and market trends—helping you make confident, informed decisions for acquisitions, dispositions, or portfolio growth.',
    bullets: [
      'Comprehensive evaluations of property performance and potential',
      'Clear, actionable reports for investors and stakeholders',
      'Support for buy/sell decisions, refinancing, and strategy'
    ]
  }
];

const Services = () => {
  return (
    <section id="services" className="section-padding bg-navy text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(#C9972B 1px, transparent 1px)', backgroundSize: '30px 30px' }}></div>
      
      <div className="container-custom relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-[2px] bg-gold"></div>
            <span className="text-gold font-semibold uppercase tracking-wider text-sm">What We Offer</span>
            <div className="w-8 h-[2px] bg-gold"></div>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Expert Valuations, Promptly Delivered.</h2>
          <p className="text-gray-300 text-lg">
            We deliver precise, objective, and timely property appraisals trusted by lenders, investors, and homeowners across Texas.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {services.map((service, idx) => (
            <motion.div 
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.2, duration: 0.6 }}
              style={{ perspective: "1000px" }}
            >
              <TiltCard className="h-full cursor-pointer">
                <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors duration-300 group flex flex-col h-full shadow-2xl">
                  <div className="w-16 h-16 bg-gold/20 rounded-xl flex items-center justify-center text-gold mb-6 group-hover:scale-110 transition-transform duration-300">
                    <SafeIcon icon={service.icon} className="w-8 h-8" />
                  </div>
                  
                  <h3 className="text-2xl font-serif font-bold text-white mb-4">{service.title}</h3>
                  
                  <p className="text-gray-300 mb-6 leading-relaxed flex-grow">
                    {service.description}
                  </p>
                  
                  <ul className="space-y-3 mb-8">
                    {service.bullets.map((bullet, bIdx) => (
                      <li key={bIdx} className="flex items-start gap-3 text-sm text-gray-200">
                        <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2 flex-shrink-0"></div>
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <a 
                    href="#contact" 
                    className="inline-flex items-center gap-2 text-gold font-medium hover:text-white transition-colors mt-auto w-fit"
                  >
                    Learn More <SafeIcon icon={FiArrowRight} />
                  </a>
                </div>
              </TiltCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;